import { useState } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import SectionHeading from '@/components/base/SectionHeading';
import { usePageMeta } from '@/hooks/usePageMeta';

const team = [
  { name: '内容编辑部', role: '剧集资讯与剧评', desc: '负责每日影视资讯的选题、撰写与校对，坚持真实、克制的表达。', icon: 'ri-edit-2-line' },
  { name: '数据资料组', role: '剧集与演员资料库', desc: '整理剧集、演员基础资料与作品关系，保证详情页信息准确一致。', icon: 'ri-database-2-line' },
  { name: '产品体验组', role: '搜索与筛选体验', desc: '打磨搜索、筛选与浏览路径，让找剧、追剧这件事更轻松。', icon: 'ri-cursor-line' },
  { name: '视觉设计组', role: '门户视觉与排版', desc: '负责全站视觉风格与信息层级，让内容阅读舒适、层次清晰。', icon: 'ri-palette-line' },
];

const values = [
  { title: '信息准确', desc: '所有剧集与资讯均来自演示资料库，字段之间保持一致。', icon: 'ri-checkbox-circle-line' },
  { title: '浏览高效', desc: '支持关键词搜索与多维筛选，快速定位想看的内容。', icon: 'ri-flashlight-line' },
  { title: '阅读友好', desc: '合理的字号与行距，PC 与移动端都能舒适阅读。', icon: 'ri-book-open-line' },
];

const faqs = [
  {
    q: '网站内容来自哪里？',
    a: '本站为演示性质的门户站点，剧集、资讯与演员资料均来自站内演示数据，用于展示产品的浏览与检索体验。',
  },
  {
    q: '是否提供在线播放或下载？',
    a: '不提供。本站仅聚合影视剧信息与行业动态，不含播放、下载、登录注册与支付等功能，也不会展示虚假的成功状态。',
  },
  {
    q: '收藏功能如何使用？',
    a: '在剧集或资讯详情页点击收藏按钮即可，收藏状态保存在你的浏览器本地，刷新页面后依然保留，再次点击即可取消收藏。',
  },
  {
    q: '如何联系我们？',
    a: '你可以通过页面底部或本页的联系邮箱与我们取得联系，我们会尽快回复。',
  },
];

export default function About() {
  usePageMeta(
    '漂亮的姐姐 - 关于我们 - 影视资讯门户',
    '了解漂亮的姐姐的定位、团队与联系方式。我们聚合最新影视剧信息与行业动态，帮助观众轻松了解和追踪喜欢的影视内容。',
  );

  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <div className="w-full max-w-page mx-auto px-4 md:px-6 py-6">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

      <section className="mt-5 rounded-lg overflow-hidden relative">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20stylized%20cinema%20theme%20banner%20with%20soft%20deep%20indigo%20and%20warm%20orange%20gradient%20waves%2C%20subtle%20film%20strip%20motifs%2C%20elegant%20minimal%20portal%20design%2C%20no%20text&width=1600&height=700&seq=about-banner&orientation=landscape"
            alt="漂亮的姐姐 品牌横幅"
            title="漂亮的姐姐 影视资讯门户"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-900/80 to-primary-900/60"></div>
        </div>
        <div className="relative px-6 py-12 md:px-10 md:py-16">
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-background-50">关于漂亮的姐姐</h1>
          <p className="mt-4 max-w-2xl text-sm md:text-base text-primary-100 leading-relaxed">
            漂亮的姐姐是一个聚合影视剧信息与行业动态的综合平台，面向广大影视爱好者、追剧用户与娱乐资讯关注者。
            我们希望通过清晰的分类、好用的搜索和扎实的内容，帮助你快速获取最新影视剧推荐，深入了解剧情故事与演员动态。
          </p>
        </div>
      </section>

      <section className="mt-10">
        <SectionHeading title="我们坚持的三件事" subtitle="内容、效率与阅读体验，缺一不可" icon="ri-compass-3-line" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {values.map((item) => (
            <div key={item.title} className="p-5 rounded-lg bg-background-50 border border-background-200">
              <span className="w-11 h-11 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center">
                <i className={`${item.icon} text-xl`}></i>
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <SectionHeading title="团队介绍" subtitle="一支专注影视内容与体验的小团队" icon="ri-team-line" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {team.map((item) => (
            <div key={item.name} className="flex gap-4 p-5 rounded-lg bg-secondary-50 border border-secondary-100">
              <span className="w-11 h-11 shrink-0 rounded-md bg-secondary-100 text-secondary-700 flex items-center justify-center">
                <i className={`${item.icon} text-xl`}></i>
              </span>
              <div>
                <h3 className="font-heading text-base font-bold text-foreground-950">{item.name}</h3>
                <p className="text-xs font-label text-secondary-700 mt-0.5">{item.role}</p>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <SectionHeading title="联系方式" subtitle="欢迎交流与合作" icon="ri-mail-line" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          <div className="p-5 rounded-lg bg-background-50 border border-background-200">
            <span className="w-10 h-10 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center">
              <i className="ri-mail-line text-lg"></i>
            </span>
            <h3 className="mt-3 font-heading text-sm font-bold text-foreground-950">邮箱</h3>
            <a href="mailto:hello@pljj-film.example" className="mt-1 block text-sm text-primary-600 hover:text-primary-700 transition-colors break-all">
              hello@pljj-film.example
            </a>
          </div>
          <div className="p-5 rounded-lg bg-background-50 border border-background-200">
            <span className="w-10 h-10 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center">
              <i className="ri-map-pin-line text-lg"></i>
            </span>
            <h3 className="mt-3 font-heading text-sm font-bold text-foreground-950">办公地点</h3>
            <p className="mt-1 text-sm text-foreground-600">上海市 · 影视文化创意园</p>
          </div>
          <div className="p-5 rounded-lg bg-background-50 border border-background-200">
            <span className="w-10 h-10 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center">
              <i className="ri-time-line text-lg"></i>
            </span>
            <h3 className="mt-3 font-heading text-sm font-bold text-foreground-950">内容更新时间</h3>
            <p className="mt-1 text-sm text-foreground-600">工作日上午 10:00 更新</p>
          </div>
        </div>
      </section>

      <section className="mt-10">
        <SectionHeading title="常见问题" subtitle="使用本站前，你可能想知道的事" icon="ri-question-line" />
        <div className="rounded-lg border border-background-200 bg-background-50 divide-y divide-background-200 overflow-hidden">
          {faqs.map((item, index) => (
            <div key={item.q}>
              <button
                type="button"
                onClick={() => setOpenFaq(openFaq === index ? null : index)}
                aria-expanded={openFaq === index}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left cursor-pointer hover:bg-background-100 transition-colors"
              >
                <span className="font-heading text-sm font-bold text-foreground-900">{item.q}</span>
                <i className={`${openFaq === index ? 'ri-subtract-line text-accent-600' : 'ri-add-line text-foreground-500'} text-lg shrink-0`}></i>
              </button>
              {openFaq === index ? (
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed animate-fade-in">{item.a}</div>
              ) : null}
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <div className="p-5 rounded-lg bg-background-100 border border-background-200">
          <h2 className="font-heading text-lg font-bold text-foreground-950 flex items-center gap-2">
            <i className="ri-file-text-line text-primary-600"></i>
            使用条款
          </h2>
          <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
            本站内容用于信息浏览与学习参考。你可以自由浏览与分享页面链接，但请勿将演示数据用于商业用途或对外声称其真实性。
            站内图片均为演示性质的生成素材。
          </p>
        </div>
        <div className="p-5 rounded-lg bg-background-100 border border-background-200">
          <h2 className="font-heading text-lg font-bold text-foreground-950 flex items-center gap-2">
            <i className="ri-shield-keyhole-line text-secondary-600"></i>
            隐私政策
          </h2>
          <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
            本站不收集你的个人账号信息，也无需登录。收藏等偏好仅保存在你自己的浏览器本地，不会上传到服务器，
            清除浏览器数据后将不再保留。
          </p>
        </div>
      </section>

      <div className="mt-10 p-6 rounded-lg bg-primary-900 text-center">
        <h2 className="font-heading text-xl font-bold text-background-50">开始探索你喜欢的影视内容</h2>
        <p className="mt-2 text-sm text-primary-100">从热门剧集到演员动态，漂亮的姐姐陪你一起追剧。</p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          <Link to="/category/tv-series" className="px-5 py-2.5 rounded-md bg-accent-500 text-background-50 dark:text-foreground-950 text-sm font-label font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap">
            浏览电视剧
          </Link>
          <Link to="/search" className="px-5 py-2.5 rounded-md border border-primary-700 text-background-100 text-sm font-label hover:bg-primary-800 transition-colors cursor-pointer whitespace-nowrap">
            去搜索内容
          </Link>
        </div>
      </div>
    </div>
  );
}